//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';
import { Placement } from 'tippy.js';

import { AlertIconWithMargin } from '@components/atoms/alert-icon/AlertIcon';
import Text from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface IOwnProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ITrackableComponent {
  alertColor?: string;
  alertPlacement?: Placement;
  alertText?: string | React.ReactElement;
  disclaimerText?: string;
  value: string | number;
}

type Props = IOwnProps & Partial<React.ComponentProps<typeof Text>>;

/**
 * The StatText atom renders a standardized line of text for a StatBubble main stat
 */
export const StatText = ({
  alertColor,
  alertPlacement,
  alertText,
  className,
  disclaimerText,
  mixin = Mixins.Text.Title,
  trackingId,
  value,
}: Props) => {
  return (
    <StatContainer className={className} trackingId={trackingId}>
      <Text mixin={mixin}>{value}</Text>
      {alertText && (
        <StyledAlertIconWithMargin
          alertText={alertText}
          color={alertColor}
          placement={alertPlacement}
        />
      )}
      {disclaimerText && (
        <SmallTooltip
          content={disclaimerText}
          placement='right'
          target={<AsteriskSpan>*</AsteriskSpan>}
        />
      )}
    </StatContainer>
  );
};

const StatContainer = withTracking(styled.div`
  display: flex;
  align-items: center;
`);

const StyledAlertIconWithMargin = styled(AlertIconWithMargin)(
  ({ theme }) => `
  position: relative;
  bottom: ${theme.spacings[1]};
`,
);

const AsteriskSpan = styled.span(
  ({ theme }) => `
    color: ${theme.colors.aliases.bodySecondary};
    margin-left: ${theme.spacings[1]};
    position: relative;
    bottom: ${theme.spacings[1]};
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes[3]};
  `,
);

export default StatText;
